import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import PhoneNumberInput from "src/components/shared/PhoneNumberInput";
import SearchFilterComp from "src/components/shared/SearchFilterComp";
import { customerType, USER_TYPE } from "src/constant";
import convertCurrency from "src/helper/convertCurrency";
import CustomerAddress from "./create/CustomerAddress";
import { Add } from "@mui/icons-material";
import CustomerBrand from "./create/CustomerBrand";
import useCustomerCreate from "src/hooks/user/customer/useCustomerCreate";
import { enqueueSnackbar } from "notistack";
import useAppGlobal from "src/context/useAppGlobal";
import useCustomerDetail from "src/hooks/user/customer/useCustomerDetail";
import useCustomerUpdate from "src/hooks/user/customer/useCustomerUpdate";
import useCustomAddressDetail from "src/hooks/user/customer/useCustomAddressDetail";

const brandTemp = {
  discount: 0,
  debtLimit: 0,
  brand: "",
};

const addressTemp = {
  recipientName: "",
  detailAddress: "",
  phone: "",
  isDefault: true,
  ward: 0,
  country: "",
  addressLevel: 2,
};

const RequiredLabel = ({ children, required = false }) => {
  return (
    <>
      {children}
      {required && (
        <span
          style={{
            color: "red",
            marginLeft: 2,
          }}
        >
          *
        </span>
      )}
    </>
  );
};

export default function CustomerCreateDialog({ item }) {
  const { closeDialog } = useAppGlobal();
  const [customerDetail, setCustomerDetail] = useState({
    type: customerType.individualCustomer,
    fullName: "",
    companyName: "",
    taxCode: "",
    email: "",
    phone: "",
    sales: "",
    debtLimit: 0,
    customVat: 0,
    vat: 0,
    customerBrands: [brandTemp],
    customerAddresses: [addressTemp],
  });
  const isBusiness = customerDetail.type !== customerType.individualCustomer;

  const { trigger: create } = useCustomerCreate();
  const { trigger: update } = useCustomerUpdate(item?._id);

  const { data: customer } = useCustomerDetail(item?._id);
  const { data: address } = useCustomAddressDetail(item?._id);

  useEffect(() => {
    if (!customer?.data) return;

    const data = customer.data;

    setCustomerDetail({
      ...data,
      type: data?.type ?? customerType.individualCustomer,
      fullName: data?.fullName ?? "",
      companyName: data?.companyName ?? "",
      taxCode: data?.taxCode ?? "",
      email: data?.email ?? "",
      phone: data?.phone ?? "",
      sales: data?.sales?._id ?? data?.sales ?? "",
      debtLimit: data?.debtLimit ?? 0,
      customVat: data?.customVat ?? 0,
      vat: data?.vat ?? 0,
      customerBrands: data?.customerBrands?.length > 0 ? data.customerBrands : [brandTemp],
      customerAddresses:
        address?.data?.length > 0
          ? address?.data?.map((v) => ({
              ...v,
              country: v?.country ?? "",
              province: v?.ward?.district?.province ?? "",
              ward: v?.ward ?? 0,
              district: v?.ward?.district ?? "",
            }))
          : [addressTemp],
    });
  }, [customer?.data, address?.data]);

  const handleChange = (name, value) => {
    setCustomerDetail((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onAddAddress = () => {
    setCustomerDetail((prev) => ({
      ...prev,
      customerAddresses: [...prev.customerAddresses, { ...addressTemp }],
    }));
  };

  const onAddBrand = () => {
    setCustomerDetail((prev) => ({
      ...prev,
      customerBrands: [...prev.customerBrands, { ...brandTemp }],
    }));
  };

  const isOverflowed = () => {
    return customerDetail?.customerBrands?.some((v) => v?.debtLimit > customerDetail?.debtLimit);
  };

  const onSubmit = async () => {
    if (isBusiness && (!customerDetail?.companyName?.trim() || !customerDetail?.taxCode?.trim())) {
      return enqueueSnackbar("Vui lòng nhập tên công ty và mã số thuế", { variant: "warning" });
    }

    const data = {
      ...customerDetail,
      customerAddresses: customerDetail?.customerAddresses?.map((v) => ({
        recipientName: v?.recipientName || undefined,
        detailAddress: v?.detailAddress || undefined,
        phone: v?.phone || undefined,
        isDefault: v?.isDefault ?? true,
        ward: v?.ward === 0 ? undefined : v?.ward?._id,
        country: v?.country?._id || undefined,
        province: v?.province?._id || undefined,
        district: v?.district?._id || undefined,
      })),
    };

    const hasMoreThanTwoDefault = customerDetail?.customerAddresses?.filter((v) => v?.isDefault === true);
    if ([0, 1]?.includes(hasMoreThanTwoDefault?.length)) {
      let res;
      if (!item) {
        res = await create({
          body: data,
        });
      } else {
        res = await update({
          body: data,
        });
      }

      if (res?.success) {
        await closeDialog();

        return enqueueSnackbar("Thành công", {
          variant: "success",
        });
      }

      return enqueueSnackbar(res?.error?.message, {
        variant: "error",
      });
    } else {
      return enqueueSnackbar("Chỉ thiết lập 1 địa chỉ mặc định", { variant: "warning" });
    }
  };

  return (
    <>
      <DialogContent>
        <Box
          display="flex"
          gap={2}
          flexDirection="column"
        >
          <FormControl>
            <RadioGroup
              row
              value={customerDetail.type}
              name="customer-type"
              onChange={(e) => handleChange("type", e.target.value)}
            >
              <FormControlLabel
                value={customerType.individualCustomer}
                control={<Radio />}
                label="Khách hàng cá nhân"
              />

              <FormControlLabel
                value={customerType.business}
                control={<Radio />}
                label="Doanh nghiệp"
              />
            </RadioGroup>
          </FormControl>

          <TextField
            label={<RequiredLabel required>Tên khách hàng</RequiredLabel>}
            fullWidth
            value={customerDetail.fullName}
            onChange={(e) => handleChange("fullName", e.target.value)}
          />

          <SearchFilterComp
            disabled={!customerDetail?.fullName}
            size="large"
            minWidth={0}
            width="100%"
            defaultValue={customerDetail?.sales || customerDetail?.sales?._id || ""}
            selectList={[
              {
                id: USER_TYPE.Sales,
                title: "Nhân viên kinh doanh",
                value: USER_TYPE.Sales,
              },
            ]}
            autoComplete={{
              [USER_TYPE.Sales]: {
                endpoint: "/api/v1/users",
                type: USER_TYPE.Sales,
                state: (e) => handleChange("sales", e),
                label: "Nhân viên kinh doanh",
              },
              sx: {
                flex: 1,
              },
            }}
          />

          <TextField
            label={<RequiredLabel required={isBusiness}>Tên công ty</RequiredLabel>}
            required={isBusiness}
            fullWidth
            value={customerDetail.companyName}
            onChange={(e) => handleChange("companyName", e.target.value)}
          />

          <TextField
            label={<RequiredLabel required={isBusiness}>Mã số thuế</RequiredLabel>}
            required={isBusiness}
            fullWidth
            value={customerDetail.taxCode}
            onChange={(e) => handleChange("taxCode", e.target.value)}
          />

          <TextField
            disabled={!customerDetail?.taxCode}
            label={<RequiredLabel required>Email</RequiredLabel>}
            fullWidth
            value={customerDetail.email}
            onChange={(e) => handleChange("email", e.target.value)}
          />

          <PhoneNumberInput
            disabled={!customerDetail?.email}
            name="phone"
            label="Số điện thoại"
            width="100%"
            required
            value={customerDetail.phone ?? ""}
            onChange={(e) => handleChange("phone", e)}
          />

          <Box>
            <NumericFormat
              disabled={!customerDetail?.phone}
              fullWidth
              value={customerDetail.debtLimit ?? ""}
              InputProps={{
                inputProps: {
                  min: 0,
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography>đ</Typography>
                  </InputAdornment>
                ),
              }}
              label={
                <span>
                  Giới hạn tổng công nợ
                  <span
                    style={{
                      color: "red",
                      marginLeft: 2,
                    }}
                  >
                    *
                  </span>
                </span>
              }
              thousandSeparator
              customInput={TextField}
              onFocus={(event) => {
                if (customerDetail?.debtLimit === 0) {
                  event.target.select();
                }
              }}
              onValueChange={(values) => handleChange("debtLimit", Number(values?.floatValue ?? 0))}
            />

            {customerDetail?.debtLimit > 0 && (
              <Typography
                mt={1}
                sx={{
                  fontStyle: "italic",
                }}
                variant="caption"
              >
                <strong>({convertCurrency(customerDetail?.debtLimit) ?? "N/A"} đồng)</strong>
              </Typography>
            )}
          </Box>

          <FormControl>
            <FormLabel>
              VAT
              <Typography
                color="error"
                component="span"
              >
                *
              </Typography>
            </FormLabel>

            <RadioGroup
              row
              value={customerDetail.customVat}
              onChange={(e) => handleChange("customVat", Number(e.target.value))}
            >
              <FormControlLabel
                value={0}
                control={<Radio />}
                label="Mặc định"
              />

              <FormControlLabel
                value={1}
                control={<Radio />}
                label="Tuỳ chỉnh"
              />
            </RadioGroup>

            {customerDetail.customVat === 1 && (
              <TextField
                label={<RequiredLabel required>VAT (%)</RequiredLabel>}
                fullWidth
                value={customerDetail.vat}
                inputProps={{
                  min: 0,
                  max: 100,
                }}
                type="number"
                onChange={(e) => handleChange("vat", Number(e.target.value))}
              />
            )}
          </FormControl>

          <Divider
            sx={{
              borderColor: "var(--grey) !important",
              width: "100%",
              mt: "24px",
            }}
          />

          <Typography
            fontWeight={700}
            variant="body1"
          >
            Thông tin địa chỉ
          </Typography>

          {customerDetail?.customerAddresses?.map((_, i) => (
            <CustomerAddress
              key={i}
              index={i}
              setCustomerDetail={setCustomerDetail}
              customerDetail={customerDetail}
              initialShow={!item}
            />
          ))}

          <Button
            variant="outlined"
            sx={{
              m: "0 0 0 auto",
            }}
            startIcon={<Add />}
            onClick={onAddAddress}
          >
            Thêm địa chỉ
          </Button>

          {customerDetail?.customerBrands?.map((_, i) => (
            <CustomerBrand
              key={i}
              index={i}
              setCustomerDetail={setCustomerDetail}
              customerDetail={customerDetail}
              debtLimit={customerDetail?.debtLimit}
            />
          ))}

          <Button
            variant="outlined"
            sx={{
              m: "0 0 0 auto",
            }}
            startIcon={<Add />}
            onClick={onAddBrand}
          >
            Thêm thương hiệu
          </Button>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          p: 2,
        }}
      >
        <Button
          onClick={onSubmit}
          disabled={isOverflowed()}
          variant="contained"
          type="button"
          fullWidth
        >
          Xác nhận
        </Button>
      </DialogActions>
    </>
  );
}
